import { Autocomplete, Box, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEntityCaptures, getManagementUnitCaptures } from "../../api/captures";
import { getEntityManagementUnits } from "../../api/managementUnits";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import Files from "./Files";

interface TabFilesProps {
  entityId: number;
  managementUnitId?: number;
  captureId?: number;
  flowTestId?: number;
  context: "entity" | "managementUnit" | "capture" | "flowTest";
}

export default function TabFiles({
  entityId,
  managementUnitId,
  captureId,
  flowTestId,
  context,
}: Readonly<TabFilesProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  const [selectedSystemId, setSelectedSystemId] = useState<number | undefined>(managementUnitId);
  const [selectedCaptureId, setSelectedCaptureId] = useState<number | undefined>(captureId);

  const [systems, setSystems] = useState<any[]>([]);
  const [captures, setCaptures] = useState<any[]>([]);

  useEffect(() => {
    const fetchManagementUnits = async () => {
      try {
        if (context === "entity" && entityId) {
          const data = await getEntityManagementUnits(entityId);
          setSystems(data);
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchManagementUnits();
  }, [context, entityId]);

  useEffect(() => {
    const fetchCapturesBySystem = async () => {
      try {
        if (
          (context === "entity" && selectedSystemId) ||
          context === "managementUnit"
        ) {
          const sysId =
            context === "managementUnit"
              ? managementUnitId
              : selectedSystemId;

          if (!sysId) return;

          const data = await getManagementUnitCaptures(sysId);
          setCaptures(data);
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchCapturesBySystem();
  }, [context, selectedSystemId, managementUnitId]);

  useEffect(() => {
    const fetchEntityCaptures = async () => {
      try {
        if (
          context === "entity" && 
          !selectedSystemId && 
          entityId
        ) {
          const data = await getEntityCaptures(entityId);
          setCaptures(data);
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchEntityCaptures();
  }, [context, entityId, selectedSystemId]);

  let effectiveSystemId: number | undefined;

  if (context === "capture") {
    effectiveSystemId = undefined;
  } else if (context === "managementUnit") {
    effectiveSystemId = managementUnitId;
  } else {
    effectiveSystemId = selectedSystemId;
  }

  if (context === "flowTest") {
    return (
      <Files
        entityId={entityId}
        captureId={captureId}
        flowTestId={flowTestId}
      />
    );
  }

  const effectiveEntityId = entityId;
  const effectiveCaptureId =
    context === "capture" ? captureId : selectedCaptureId;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {context === "entity" && (
        <Box sx={{ display: "flex", gap: 2 }}>
          <Autocomplete
            fullWidth
            options={systems}
            getOptionLabel={(opt) => opt.name ?? opt.desig ?? ""}
            value={systems.find((s) => s.id === selectedSystemId) ?? null}
            onChange={(_, newValue) => {
              setSelectedSystemId(newValue?.id ?? undefined);
              setSelectedCaptureId(undefined);
            }}
            renderInput={(params) => (
              <TextField {...params} label={t("management-units.label")} />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name ?? option.desig}
              </li>
            )}
            noOptionsText={t("generic.no-options")}
          />

          <Autocomplete
            fullWidth
            options={captures}
            getOptionLabel={(opt) => opt.name ?? opt.desig ?? ""}
            value={captures.find((c) => c.id === selectedCaptureId) ?? null}
            onChange={(_, newValue) => {
              setSelectedCaptureId(newValue?.id ?? undefined);
            }}
            renderInput={(params) => (
              <TextField {...params} label={t("captures.label")} />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name ?? option.desig}
              </li>
            )}
            noOptionsText={t("generic.no-options")}
          />
        </Box>
      )}

      {context === "managementUnit" && (
        <Autocomplete
          fullWidth
          options={captures}
          getOptionLabel={(opt) => opt.name ?? opt.desig ?? ""}
          value={captures.find((c) => c.id === selectedCaptureId) ?? null}
          onChange={(_, newValue) => {
            setSelectedCaptureId(newValue?.id ?? undefined);
          }}
          renderInput={(params) => (
            <TextField {...params} label={t("captures.label")} />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name ?? option.desig}
            </li>
          )}
          noOptionsText={t("generic.no-options")}
        />
      )}

      <Files
        entityId={effectiveEntityId}
        managementUnitId={effectiveSystemId}
        captureId={effectiveCaptureId}
      />
    </Box>
  );
}